import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { NavLink, useLocation, useSearchParams } from 'react-router-dom';
import { createGlobalState } from 'react-use';
import styled from 'styled-components';

import Select, { SingleValue } from 'react-select';
import {
  LANGUAGE_OPTIONS,
  LanguageOption,
  useLanguage,
} from 'src/renderer/context/Language';

import { Customer } from '@/types/Customer';
import { useCurrentAuthSession } from '@/context/AuthContext';
import { useCustomerData } from '../../features/_data/useCustomerData';
import { useUserInfo } from '../../features/_data/useUserInfo';
import { useLogout } from '../../features/AuthProvider/AuthProvider';
import {
  IconHelp,
  IconLogout,
  IconPlusWhite,
  IconTrihowLogo,
} from '../../icons';
import { Button } from '../Button/Button';
import { Flex } from '../Flex/Flex';
import { Modal } from '../Modal/Modal';
import { AddCustomerModal } from './CustomerModal/AddCustomerModal';
import { Tooltip } from '../Tooltip/Tooltip';

export const NAVBAR_HEIGHT = '72px';

const StyledNavbar = styled.nav`
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 8px 36px;
  height: ${NAVBAR_HEIGHT};
  background: ${({ theme }) => theme.color('#373737')};
  z-index: ${({ theme }) => theme.zindex.nav};
`;

const StyledFlex = styled(Flex)`
  button {
    color: ${({ theme }) => theme.color('#FFFFFF')};
  }
`;

const StyledCustomerControl = styled.div`
  display: flex;
  gap: 8px;
  button {
    min-height: 2.375rem;
  }
  & div.selected-option {
    min-height: 100%;
    display: grid;
    align-items: center;
    padding-left: 0.5rem;
  }
`;

export const useSelectedCustomerId = createGlobalState<string | undefined>();

export const Navbar = ({ isAdmin }: { readonly isAdmin: boolean }) => {
  const { isLoggedIn } = useCurrentAuthSession();
  const { logout } = useLogout();
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const { isAdmin: isUserAdmin } = useUserInfo();
  const [selectedCustomerId, setSelectedCustomerId] = useSelectedCustomerId();

  const { data: remoteCustomerData, isLoading } = useCustomerData(isUserAdmin);
  const [customerData, setCustomerData] = useState(
    remoteCustomerData ? [...remoteCustomerData] : [],
  );
  const [customerInit, setCustomerInit] = useState(false);

  const customerOptions = customerData
    ?.filter((customer: Customer) => customer)
    .map(({ id, name }: Customer) => ({
      value: id,
      label: name,
    }));
  type CustomerOption = (typeof customerOptions)[number];

  const [shouldShowSupportModal, setShouldShowSupportModal] = useState(false);
  const [shouldShowAddCustomerModal, setShouldShowAddCustomerModal] =
    useState(false);

  const { language, handleLanguageChange } = useLanguage();
  const initiallySelected =
    LANGUAGE_OPTIONS.find((option) => option.value === language) ?? null;
  const [selectedOption, setSelectedOption] = useState<LanguageOption | null>(
    initiallySelected,
  );

  const handleLanguageSelect = (option: SingleValue<LanguageOption>) => {
    setSelectedOption(option);
    handleLanguageChange(option?.value ?? 'DE');
  };

  const handleSelect = (option: SingleValue<CustomerOption>) => {
    setParams((prev) => ({ ...prev, customerId: option?.value }));
    setSelectedCustomerId(option?.value);
  };

  useEffect(() => {
    if (remoteCustomerData && remoteCustomerData.length) {
      if (!customerInit && remoteCustomerData.length !== customerData.length) {
        const newCustomerList =
          remoteCustomerData.length > customerData.length
            ? remoteCustomerData
            : customerData;
        setCustomerData([...newCustomerList]);
        setCustomerInit(true);
      }

      const initialCustomerId =
        remoteCustomerData.find(
          (customer: Customer) => customer?.name === 'Trihow AG',
        )?.id ?? (remoteCustomerData[0]?.id as string);

      if (!selectedCustomerId) {
        setParams((prev) => ({
          ...prev,
          customerId: params.get('customerId') ?? initialCustomerId,
        }));
        return setSelectedCustomerId(
          params.get('customerId') ?? initialCustomerId,
        );
      }

      setParams((prev) => ({ ...prev, customerId: selectedCustomerId }));
      return setSelectedCustomerId(selectedCustomerId);
    }

    return undefined;
  }, [customerInit, remoteCustomerData, customerData, location.pathname]);

  return (
    <StyledNavbar data-testid="nav-bar-wrapper">
      <Flex align="center" justify="space-between">
        <Flex align="center" gap={16}>
          <NavLink to="/">
            <IconTrihowLogo />
          </NavLink>

          {isAdmin && Array.isArray(customerData) ? (
            <StyledCustomerControl data-test="customer-controls">
              <Select
                captureMenuScroll
                styles={{
                  control: (base, state) => ({
                    ...base,
                    background: 'transparent',
                    border: 'none',
                    outline: `${state.isFocused ? '1px solid #239AE7' : 'none'}`,
                    cursor: 'pointer',
                  }),
                  singleValue: (base) => ({
                    ...base,
                    color: '#FFFFFF',
                  }),
                  menu: (base) => ({
                    ...base,
                    background: '#373737',
                    color: '#FFFFFF',
                    width: 'max-content',
                  }),
                  input: (base) => ({
                    ...base,
                    color: '#FFFFFF',
                  }),
                  dropdownIndicator: (base) => ({
                    ...base,
                    ':hover': {
                      color: '#FFFFFF',
                    },
                  }),
                  option: (base, state) => ({
                    ...base,
                    background:
                      state.isSelected || state.isFocused
                        ? '#239AE7'
                        : 'transparent',
                    cursor: 'pointer',
                  }),
                }}
                isLoading={isLoading}
                onChange={handleSelect}
                value={customerOptions?.find(
                  (option) => option.value === selectedCustomerId,
                )}
                options={customerOptions}
              />
              <Tooltip
                title={
                  <FormattedMessage defaultMessage="Add Customer" id="MDPHxV" />
                }
              >
                <Button
                  mode="transparent"
                  size="minimum"
                  onClick={() => {
                    setShouldShowAddCustomerModal(true);
                  }}
                  testId="add-customer"
                >
                  <IconPlusWhite />
                </Button>
              </Tooltip>
              {shouldShowAddCustomerModal ? (
                <AddCustomerModal
                  customerData={customerData}
                  setCustomerData={setCustomerData}
                  setShouldShowAddCustomerModal={setShouldShowAddCustomerModal}
                />
              ) : null}
            </StyledCustomerControl>
          ) : null}
        </Flex>
        <StyledFlex align="center" gap={24}>
          <div data-testid="select-language">
            <Select
              isSearchable={false}
              isClearable={false}
              captureMenuScroll
              styles={{
                control: (base, state) => ({
                  ...base,
                  background: 'transparent',
                  border: 'none',
                  outline: `${state.isFocused ? '1px solid #239AE7' : 'none'}`,
                  cursor: 'pointer',
                  padding: '0 4px', // Reduced padding here
                }),
                valueContainer: (base) => ({
                  ...base,
                  padding: '0', // Remove padding from value container
                }),
                singleValue: (base) => ({
                  ...base,
                  color: '#FFFFFF',
                  margin: '0', // Remove margin from the selected value
                }),
                dropdownIndicator: (base) => ({
                  ...base,
                  padding: '0 4px', // Reduced padding for the indicator
                  ':hover': {
                    color: '#FFFFFF',
                  },
                }),
                menu: (base) => ({
                  ...base,
                  background: '#373737',
                  color: '#FFFFFF',
                }),
                option: (base, state) => ({
                  ...base,
                  cursor: 'pointer',
                  background: state.isFocused ? '#239AE7' : 'transparent',
                }),
                indicatorSeparator: (base) => ({
                  ...base,
                  display: 'none',
                }),
              }}
              value={selectedOption}
              onChange={handleLanguageSelect}
              options={LANGUAGE_OPTIONS.filter(
                (option) => option.value !== selectedOption?.value,
              )}
              classNamePrefix="select"
            />
          </div>
          <Tooltip
            title={<FormattedMessage defaultMessage="Help" id="SENRqu" />}
          >
            <Button
              mode="transparent"
              size="minimum"
              onClick={() => setShouldShowSupportModal(true)}
            >
              <IconHelp />
            </Button>
          </Tooltip>
          {isLoggedIn ? (
            <Button mode="transparent" size="minimum" onClick={() => logout()}>
              <IconLogout />
            </Button>
          ) : null}
        </StyledFlex>
      </Flex>

      {shouldShowSupportModal ? (
        <Modal
          onClose={() => setShouldShowSupportModal(false)}
          title={<FormattedMessage defaultMessage="Support" id="HqRNN8" />}
        >
          <div>
            <FormattedMessage
              defaultMessage="If you would like to talk to our support please write to {contactLink}"
              id="EY2q94"
              values={{
                contactLink: (
                  <a href="mailto:support@trihow.com">support@trihow.com</a>
                ),
              }}
            />
          </div>
        </Modal>
      ) : null}
    </StyledNavbar>
  );
};
